// Google Maps helpers. All map UI degrades gracefully when no API key is set:
// callers must check mapsAvailable() and provide a non-map fallback.
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const CARACAS = { lat: 10.4806, lng: -66.9036 };
let loaderPromise = null;

// Google llama window.gm_authFailure cuando la API key/dominio no está autorizado
// (p.ej. en el preview por restricción de referrer). Esto ocurre DESPUÉS de crear
// el mapa, así que avisamos a los componentes para que muestren un fallback limpio
// en vez del recuadro "Oops" que inyecta Google.
let authFailed = false;
const authSubs = new Set();
if (typeof window !== "undefined") {
  window.gm_authFailure = () => {
    authFailed = true;
    authSubs.forEach((fn) => fn());
  };
}

export function mapsAvailable() {
  return !!KEY;
}

function loadMaps() {
  if (!KEY) return Promise.reject(new Error("no-key"));
  if (window.google && window.google.maps) return Promise.resolve(window.google);
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    // async+defer: no bloquea el render inicial de la página (apto para 3G).
    s.src = `https://maps.googleapis.com/maps/api/js?key=${KEY}&libraries=places`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.google);
    s.onerror = () => reject(new Error("load-failed"));
    document.head.appendChild(s);
  });
  return loaderPromise;
}

// Interactive pin picker for the seeker form. Calls onChange({lat,lng}).
export function MapPicker({ lat, lng, onChange }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const markerRef = useRef(null);
  const [state, setState] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let active = true;
    if (authFailed) { setState("error"); return; }
    const onFail = () => active && setState("error");
    authSubs.add(onFail);
    loadMaps().then((google) => {
      if (!active || !ref.current) return;
      const center = lat && lng ? { lat, lng } : CARACAS;
      const map = new google.maps.Map(ref.current, {
        center, zoom: lat ? 15 : 11, disableDefaultUI: true, zoomControl: true,
      });
      const place = (pos) => {
        if (markerRef.current) markerRef.current.setMap(null);
        markerRef.current = new google.maps.Marker({ position: pos, map });
        onChange({ lat: pos.lat, lng: pos.lng });
      };
      if (lat && lng) place({ lat, lng });
      map.addListener("click", (e) => place({ lat: e.latLng.lat(), lng: e.latLng.lng() }));
      if (!authFailed) setState("ready");
    }).catch(() => setState("error"));
    return () => { active = false; authSubs.delete(onFail); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative">
      <div ref={ref} data-testid="map-picker" className="w-full h-56 rounded-sm border border-neutral-300 bg-neutral-100" />
      {state !== "ready" && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-neutral-500 px-3 text-center bg-neutral-100 rounded-sm">
          {state === "loading" ? t("map.mapLoading") : t("map.mapUnavailable")}
        </div>
      )}
    </div>
  );
}

// Public detail view: centers on the GENERAL municipality area only (geocoded by
// name, low zoom, NO exact marker) so the precise pin is never exposed publicly.
export function ZoneMap({ municipality }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let active = true;
    if (authFailed) { setState("error"); return; }
    const onFail = () => active && setState("error");
    authSubs.add(onFail);
    loadMaps().then((google) => {
      if (!active || !ref.current) return;
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: `${municipality}, Venezuela` }, (results, status) => {
        if (!active || !ref.current || authFailed) return;
        if (status === "OK" && results[0]) {
          new google.maps.Map(ref.current, { center: results[0].geometry.location, zoom: 12, disableDefaultUI: true });
          setState("ready");
        } else {
          // Geocoding API no habilitada o sin resultado: no mostramos un mapa engañoso.
          setState("error");
        }
      });
    }).catch(() => setState("error"));
    return () => { active = false; authSubs.delete(onFail); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipality]);

  return (
    <div className="relative">
      <div ref={ref} data-testid="zone-map" className="w-full h-48 rounded-sm border border-neutral-300 bg-neutral-100" />
      {state !== "ready" && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-neutral-500 px-3 text-center bg-neutral-100 rounded-sm">
          {state === "loading" ? t("map.mapLoading") : t("map.zoneApprox", { muni: municipality })}
        </div>
      )}
    </div>
  );
}

